import { takeLatest, delay } from 'redux-saga'
import { call, put, select, spawn } from 'redux-saga/effects'
import callAny from './helpers/call_any'
import t from '../actions/actionTypes'
import { userSync, schoolSync, userAdvancedSettingsSync, iconSync } from '../../sync'
import { myAccountSelectors } from 'redux/selectors'
import fileResizer from 'helpers/fileResizer'
import fileUploader from 'helpers/fileUploader'
import iconTypes from 'helpers/enums/iconTypes'
import myAccountActions from 'redux/actions/myAccountActions'
import mixpanel from 'helpers/mixpanel'
import userDataSelector from 'redux/selectors/user/user_data_selector'

function *getMyAccountData() {
	try {
		const response = yield call(userSync.getUserMyAccountData)
		if (response.error_code === 0) {
			yield put({
				type: t.MY_ACCOUNT_DATA_FETCHED,
				payload: response.payload,
			})
		} else {
			yield put({
				type: t.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *savePersonalInfo(action) {
	try {
		const response = yield call(userSync.savePersonalInfo, action.firstName,
			action.lastName, action.email, action.password)
		yield put({
			type: t.OPEN_TOP_INFORMATION,
			messageObject: {
				message: response.message,
				timeout: 3000,
				styleType: (!response.error_code)?'success':'error',
			},
		})
		if (response.error_code === 0) {
			yield put({
				type: t.MY_ACCOUNT_SAVE_PERSONAL_INFO_SUCCESS,
				firstName: action.firstName,
				lastName: action.lastName,
				email: action.email,
			})
			action.resolve()
		} else if (response.error_code === 1) {
			const errors = {}
			for (const key of Object.keys(response.payload.errors)) {
				errors[key] = response.payload.errors[key]['errorText']
			}
			action.reject(errors)
			yield put({
				type: t.MY_ACCOUNT_SAVE_PERSONAL_INFO_FAILED,
				errors: response.payload.errors,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *changePassword(action) {
	try {
		const response = yield callAny(userSync.changePassword, action.oldPassword, action.newPassword, action.newPasswordRepeat)
		action.resolve()
		yield put(myAccountActions.changePasswordSuccess(response.message))
	} catch (error) {
		action.reject(error)
		yield put(myAccountActions.changePasswordFailed(error.payload.errors))
	}
}

function *saveTeacherInfo(action) {
	try {
		const response = yield call(userSync.saveTeacherInfo, action.gradeIds, action.subjectIds)
		yield put({
			type: t.OPEN_TOP_INFORMATION,
			messageObject: {
				message: response.message,
				timeout: 3000,
				styleType: (!response.error_code)?'success':'error',
			},
		})
		if (response.error_code === 0) {
			yield put({
				type: t.MY_ACCOUNT_SAVE_TEACHER_INFO_SUCCESS,
				gradeIds: action.gradeIds,
				subjectIds: action.subjectIds,
			})
		} else if (response.error_code === 1) {
			yield put({
				type: t.MY_ACCOUNT_SAVE_TEACHER_INFO_FAILED,
			})
		}
	} catch (error) {

	}
}

function *saveSchoolInfo(action) {
	try {
		yield callAny(userSync.saveSchoolInfo, action.countryId,
			action.stateId, action.institutionId, action.institutionName, action.city)

		yield put(myAccountActions.saveSchoolInfoSuccess(action.countryId,
			action.stateId, action.city, action.institutionId, action.institutionName))

		if (action.resolve) {
			action.resolve()
		}
	} catch (error) {
		if (error.serverError) {
			yield put(myAccountActions.saveSchoolInfoFailed(error.serverError.message))
		}
		if (action.reject) {
			action.reject()
		}
	}
}

function *searchSchools(action) {
	try {
		yield call(delay, 500)
		const response = yield call(schoolSync.searchSchools,
			action.search,
			action.countryId,
			action.stateId,
			true,
			action.checkRolePermission)
		if (response.error_code === 0) {
			yield put({
				type: t.MY_ACCOUNT_SCHOOLS_FETCHED,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *saveUserAdvancedSettingsData(action) {
	try {
		const advancedViewSelectorData = yield select(myAccountSelectors.advancedViewSelector)
		const setting = advancedViewSelectorData.advancedSettings[action.index].key
		const value = advancedViewSelectorData.advancedSettings[action.index].value
		if (setting === 'immersiveReaderEnabled') {
			yield spawn(trackImmersiveReaderEnabled, value)
		} else if (setting === 'enableStudentNamesToAutofill') {
			yield spawn(trackStudentNameAutofillEnabled, value)
		}
		const response = yield call(userAdvancedSettingsSync.saveUserAdvancedSettingsData, setting, value)
		if (response.error_code === 0) {
			yield put({
				type: t.MY_ACCOUNT_SAVE_ADVANCED_SETTINGS_SUCCESS,
				setting,
				value,
			})
		} else if (response.error_code === 1) {
			yield put({
				type: t.MY_ACCOUNT_SAVE_ADVANCED_SETTINGS_FAILED,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *getUserAdvancedSettingsData() {
	try {
		const response = yield call(userAdvancedSettingsSync.getUserAdvancedSettingsData)

		if (response.error_code === 0) {
			yield put({
				type: t.MY_ACCOUNT_GET_ADVANCED_SETTINGS_SUCCESS,
				payload: response.payload,
			})
		} else if (response.error_code === 1) {
			yield put({
				type: t.MY_ACCOUNT_GET_ADVANCED_SETTINGS_FAILED,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *saveIcon(action) {
	try {
		const validateResponse = yield callAny(iconSync.validateFilesPicked, action.allowedTypes, action.files['files'])
		const payload = validateResponse.payload.icons[0]
		if (payload.error_code === 0) {
			let featureId = action.featureId
			if (featureId === undefined) {
				const saveIconSelectorData = yield select(myAccountSelectors.saveIconSelector)
				featureId = saveIconSelectorData.userId
			}
			const file = action.files['files'][0]
			yield put({
				type: t.MY_ACCOUNT_UPLOADER_VALIDATE_SUCCESS,
				link: file.link,
			})
			const uploadResponse = yield callAny(fileUploader, payload.uploadId, file, 'User', featureId, iconTypes.ENTITY_ICON_TYPE_DEFAULT, action.allowedTypes)
			if (uploadResponse.error_code === 0) {
				const userData = yield select(userDataSelector)
				yield put(myAccountActions.saveIconSuccess(uploadResponse.payload.icon, userData.userId))
			}
		} else {
			yield put({
				type: t.MY_ACCOUNT_UPLOADER_VALIDATE_ERROR,
				message: validateResponse.payload[0].message,
			})
		}
	} catch (error) {
		yield put({
			type: t.MY_ACCOUNT_UPLOADER_UPLOAD_ERROR,
			message: error.serverError.message,
		})
	}
}

function *transformImage(action) {
	try {
		const imageData = yield call(fileResizer, action.link, 172, 172, 'cover')
		yield put({
			type: t.MY_ACCOUNT_NEARPOD_PICKER_FILE_PREVIEW,
			image: imageData,
		})
	} catch (err) {

	}
}

function *trackImmersiveReaderEnabled(value) {
	try {
		const properties = {}
		properties['Immersive Reader Enabled'] = value === '1'
		yield call(mixpanel.track, 'Immersive Reader Enabled', properties, false)
	} catch (err) {}
}

function *trackStudentNameAutofillEnabled(value) {
	try {
		const properties = {}
		properties['Enabled Verified Student Names'] = (value === '1')
		yield call(mixpanel.track, 'Enabled Verified Student Names', properties, false)
	} catch (err) {}
}

export default [
	takeLatest([
		t.MY_ACCOUNT_INITIALIZE_BASE, t.ONBOARDING_INITIALIZE,
		t.ADMIN_ONBOARDING_INITIALIZE_SCHOOL_VIEW,
		t.STOREFRONT_OWNER_INITIALIZE,
		t.ADMIN_ONBOARDING_INITIALIZE_CONFIRM_SCHOOL_VIEW,
		t.MY_LIBRARY_GRADES_SUBJECTS_INITIALIZE,
		t.USER_SCHOOL_PROFILE_INITIALIZE,
	], getMyAccountData),
	takeLatest(t.MY_ACCOUNT_SAVE_PERSONAL_INFO, savePersonalInfo),
	takeLatest(t.MY_ACCOUNT_SAVE_SCHOOL_INFO, saveSchoolInfo),
	takeLatest([
		t.MY_ACCOUNT_SAVE_TEACHER_INFO,
		t.STOREFRONT_ADD_GRADES_SUBJECT_SAVE,
		t.MY_LIBRARY_GRADES_SUBJECTS_SUBMIT,
	], saveTeacherInfo),
	takeLatest(t.MY_ACCOUNT_SEARCH_SCHOOLS, searchSchools),
	takeLatest(t.MY_ACCOUNT_UPLOAD_PROFILE_IMAGE, saveIcon),
	takeLatest(t.MY_ACCOUNT_UPLOADER_VALIDATE_SUCCESS, transformImage),
	takeLatest(t.MY_ACCOUNT_SAVE_ADVANCED_SETTINGS, saveUserAdvancedSettingsData),
	takeLatest(t.MY_ACCOUNT_INITIALIZE_ADVANCED_SETTINGS, getUserAdvancedSettingsData),
	takeLatest(t.MY_ACCOUNT_CHANGE_PASSWORD, changePassword),
]

export {
	getMyAccountData,
	savePersonalInfo,
	saveTeacherInfo,
	saveSchoolInfo,
	searchSchools,
	saveUserAdvancedSettingsData,
	getUserAdvancedSettingsData,
	saveIcon,
	transformImage,
	changePassword,
}
